import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import TuneIcon from "@mui/icons-material/Tune";
import { ClickAwayListener, IconButton, Paper, Tooltip } from "@mui/material";
import * as React from "react";
import { ContextMenu } from "./RampCanvasV2.types";

type Props = {
  contextMenu: ContextMenu | null;
  onClose: () => void;
  onDuplicate: () => void;
  onCopy: () => void;
  onDelete: () => void;
  onToggleLock: () => void;
  onOpenProperties: () => void;
  isLocked: boolean;
  /** Container element to constrain menu position within */
  containerRef?: React.RefObject<HTMLElement>;
};

const MENU_WIDTH = 180;
const MENU_HEIGHT = 48;

export const ShapeContextMenu: React.FC<Props> = ({
  contextMenu,
  onClose,
  onDuplicate,
  onCopy,
  onDelete,
  onToggleLock,
  onOpenProperties,
  isLocked,
  containerRef,
}) => {
  if (!contextMenu) {
    return null;
  }

  // Calculate position to keep menu in viewport (using fixed positioning with viewport coordinates)
  const menuX = Math.min(contextMenu.x, window.innerWidth - MENU_WIDTH - 10);
  const menuY = Math.min(contextMenu.y, window.innerHeight - MENU_HEIGHT - 10);

  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDuplicate();
    onClose();
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCopy();
    onClose();
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
    onClose();
  };

  const handleToggleLock = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleLock();
    onClose();
  };

  const handleOpenProperties = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpenProperties();
    onClose();
  };

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Paper
        elevation={8}
        sx={{
          position: "fixed",
          left: menuX,
          top: menuY,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderRadius: 1,
        }}
        onContextMenu={(e) => e.preventDefault()}
      >
        <Tooltip title="Copy">
          <IconButton size="small" onClick={handleCopy}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Duplicate">
          <IconButton size="small" onClick={handleDuplicate}>
            <CopyAllIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete">
          <IconButton
            size="small"
            onClick={handleDelete}
            sx={{
              color: "error.main",
              "&:hover": {
                backgroundColor: "error.light",
                color: "error.contrastText",
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={isLocked ? "Unlock" : "Lock"}>
          <IconButton size="small" onClick={handleToggleLock}>
            {isLocked ? (
              <LockIcon fontSize="small" />
            ) : (
              <LockOpenIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Properties">
          <IconButton size="small" onClick={handleOpenProperties}>
            <TuneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>
    </ClickAwayListener>
  );
};
